import {CaretLeft} from '@phosphor-icons/react';
import {Box, Link} from '@mui/material';
import {messages} from '@smartmixin/i18n';
import {useIntl} from 'react-intl';
import {Outlet, useLocation} from 'react-router';
import {PageHeader} from '../../../components/ui';
import RouterLink from '../../../lib/RouterLink';
import {useConnectSourceState} from '../../../screens/settings/services/connectSource';
import {SERVICES_PATH} from '../../../screens/settings/services/providers';
import {NavGroup, NavLink} from '../../layout/NavLink';
import {useSettingsSections} from './useSettingsSections';

/**
 * Settings. One tree for every width; CSS picks the visible piece, as the
 * shell does.
 *
 * - `/app/settings/` (phone): h1 "Settings" over the section index
 *   (SettingsIndex; from 900px it lands on Preferences instead).
 * - A section, phone: a back link to the index above an h1 naming the
 *   section — the drill-in. Below the section's own page, the back link
 *   goes to the section instead.
 * - A section, ≥900: h1 "Settings" and a side <nav> of the sections, the
 *   current one marked aria-current="page".
 */
const Component = () => {
  const {formatMessage} = useIntl();
  const {pathname} = useLocation();
  const sections = useSettingsSections();
  // Back up to Weather Services keeps the GA connect source (#379).
  const connectSource = useConnectSourceState();
  const current = sections.find((section) => section.isActive(pathname));
  const title = formatMessage(messages.settings.settings);
  // Below a section's own page (a provider under Weather Services, Premium's
  // success page) the phone back link goes up to the section, not the index.
  const trim = (path: string) => path.replace(/\/+$/, '');
  const back =
    current && trim(pathname) !== trim(current.to)
      ? {to: trim(current.to), label: current.label}
      : {to: '/app/settings/', label: title};

  if (!current) {
    return (
      <>
        <PageHeader title={title} />
        <Outlet />
      </>
    );
  }

  return (
    <>
      <Box
        data-testid="settings-phone-header"
        sx={{display: {xs: 'block', md: 'none'}}}>
        <PageHeader
          title={current.label}
          breadcrumbs={
            <Link
              component={RouterLink}
              to={back.to}
              state={back.to === SERVICES_PATH ? connectSource : undefined}
              underline="hover"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                minHeight: 32,
                mb: 0.5,
                typography: 'body2',
                color: 'ink.main',
                fontWeight: 'fontWeightMedium',
              }}>
              <CaretLeft aria-hidden />
              {back.label}
            </Link>
          }
        />
      </Box>
      <Box sx={{display: {xs: 'none', md: 'block'}}}>
        <PageHeader title={title} />
      </Box>
      <Box sx={{display: 'flex', gap: {md: 3, lg: 4}}}>
        {/*
          Flat on the page, like the app sidebar: the section list is
          navigation, not content, so it gets no card.
        */}
        <Box
          data-testid="settings-side-nav"
          sx={{
            display: {xs: 'none', md: 'block'},
            width: 220,
            flexShrink: 0,
            height: 'fit-content',
            position: 'sticky',
            top: 16,
          }}>
          <Box component="nav" aria-label={title}>
            <NavGroup>
              {sections.map((section) => (
                <NavLink key={section.key} item={section} />
              ))}
            </NavGroup>
          </Box>
        </Box>
        <Box sx={{flex: 1, minWidth: 0}}>
          <Outlet />
        </Box>
      </Box>
    </>
  );
};

Component.displayName = 'Settings';

export default Component;
